/**
 * TurnstileCaptcha - Cloudflare Turnstile CAPTCHA Widget
 * A privacy-friendly, invisible CAPTCHA for human verification
 * 
 * Mobile-optimized with retry logic and better error handling
 */

import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Loader2, Shield, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect mobile browser
const isMobileBrowser = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Load Turnstile script with retry logic for mobile
const loadTurnstileScript = (retryCount = 0) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;

  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const existingScript = document.getElementById('cf-turnstile-script');
    if (existingScript) {
      // Script exists but may not be loaded yet
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkLoaded);
          resolve(window.turnstile);
        }
      }, 100);
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (window.turnstile) {
          resolve(window.turnstile);
        } else if (retryCount < MAX_RETRIES) {
          // Remove failed script and retry
          existingScript.remove();
          setTimeout(() => {
            loadTurnstileScript(retryCount + 1).then(resolve).catch(reject);
          }, RETRY_DELAY);
        } else {
          reject(new Error('Failed to load Turnstile script after retries'));
        }
      }, 5000);
      return;
    }

    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Small delay for mobile browsers to initialize
      setTimeout(() => {
        if (window.turnstile) {
          resolve(window.turnstile);
        } else if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            loadTurnstileScript(retryCount + 1).then(resolve).catch(reject);
          }, RETRY_DELAY);
        } else {
          reject(new Error('Turnstile object not available after script load'));
        }
      }, isMobileBrowser() ? 500 : 100);
    };
    
    script.onerror = () => {
      if (retryCount < MAX_RETRIES) {
        script.remove();
        setTimeout(() => {
          loadTurnstileScript(retryCount + 1).then(resolve).catch(reject);
        }, RETRY_DELAY);
      } else {
        reject(new Error('Failed to load Turnstile script'));
      }
    };
    
    document.head.appendChild(script);
  });
};

const TurnstileCaptcha = forwardRef(({
  siteKey,
  onVerify,
  onError,
  onExpire,
  action = 'submit',
  theme = 'auto',
  size = 'normal',
  appearance = 'always',
  className = ''
}, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading, ready, verified, error
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const isMobile = isMobileBrowser();

  // Use refs for callbacks to avoid re-render loop
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  useEffect(() => { onVerifyRef.current = onVerify; }, [onVerify]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const handleCallback = useCallback((token) => {
    // Debug: surfaces when Cloudflare marks the widget as verified. Kept
    // permanently (production too) so intermittent failures leave a trail.
    console.info('[TurnstileCaptcha] verified, token length=', token ? token.length : 0);
    setStatus('verified');
    setRetryCount(0);
    if (onVerifyRef.current) onVerifyRef.current(token);
  }, []);

  const handleError = useCallback((errorCode) => {
    console.warn('Turnstile error:', errorCode);
    setStatus('error');
    setError(errorCode);
    if (onErrorRef.current) onErrorRef.current(errorCode);
  }, []);

  const handleExpire = useCallback(() => {
    setStatus('ready');
    if (onExpireRef.current) onExpireRef.current();
  }, []);

  const handleRetry = useCallback(() => {
    setStatus('loading');
    setError(null);
    setRetryCount(0);
    
    // Remove existing widget
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      } catch (e) {
        // Widget may already be removed
      }
    }
    
    // Clear container and reinitialize
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    
    // Trigger re-render by updating state
    setTimeout(() => {
      initWidget();
    }, 500);
  }, []);

  const initWidget = useCallback(async () => {
    if (!siteKey || !containerRef.current) return;

    try {
      const turnstile = await loadTurnstileScript();
      
      if (!containerRef.current) return;

      // Remove any existing widget
      if (widgetIdRef.current !== null) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be removed
        }
      }

      // Clear container for mobile
      if (isMobile) {
        containerRef.current.innerHTML = '';
      }

      // Render new widget with mobile-optimized settings
      const widgetConfig = {
        sitekey: siteKey,
        callback: handleCallback,
        'error-callback': handleError,
        'expired-callback': handleExpire,
        action,
        theme,
        size: isMobile ? 'compact' : size,
        appearance,
        retry: 'auto',
        'retry-interval': 2000,
        'refresh-expired': 'auto',
        execution: 'render'
      };

      widgetIdRef.current = turnstile.render(containerRef.current, widgetConfig);
      setStatus('ready');
    } catch (err) {
      console.error('Turnstile init error:', err);
      setStatus('error');
      setError('init_failed');
      if (onErrorRef.current) onErrorRef.current('init_failed');
    }
  }, [siteKey, action, theme, size, appearance, isMobile, handleCallback, handleError, handleExpire]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!mounted) return;
      await initWidget();
    };

    init();

    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be removed
        }
      }
    };
  }, [initWidget]);

  // Reset method exposed to parent
  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setStatus('ready');
    }
  }, []);

  // Expose reset method
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.reset = reset;
    }
  }, [reset]);

  // Expose imperative API to parent components (register submit uses this as a
  // fallback in case the onVerify callback race-condition'd out of sync with
  // the widget's real state).
  useImperativeHandle(ref, () => ({
    /** Read the token straight from Cloudflare's widget. Returns undefined if
     * widget is not verified or has expired. */
    getResponse: () => {
      if (widgetIdRef.current === null || !window.turnstile) return undefined;
      try {
        return window.turnstile.getResponse(widgetIdRef.current);
      } catch (e) {
        console.warn('[TurnstileCaptcha] getResponse failed', e);
        return undefined;
      }
    },
    reset,
    getStatus: () => status,
  }), [reset, status]);

  return (
    <div className={`turnstile-wrapper ${className}`} data-testid="turnstile-captcha">
      {status === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading verification...</span>
        </div>
      )}
      
      <div 
        ref={containerRef} 
        data-testid="turnstile-widget"
        className={status === 'loading' ? 'hidden' : ''}
        style={{ minHeight: isMobile ? '65px' : '70px' }}
      />

      {status === 'verified' && (
        <div className="flex items-center gap-2 text-sm text-green-600 mt-2" data-testid="captcha-verified">
          <ShieldCheck className="w-4 h-4" />
          <span>Verified</span>
        </div>
      )}

      {status === 'error' && (
        <div className="flex flex-col items-center gap-2 text-sm mt-2" data-testid="captcha-error">
          <div className="flex items-center gap-2 text-red-600">
            <ShieldAlert className="w-4 h-4" />
            <span>Verification failed</span>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
            data-testid="captcha-retry-btn"
          >
            <RefreshCw className="w-3 h-3" />
            Try again
          </button>
          {isMobile && (
            <p className="text-xs text-muted-foreground text-center mt-1">
              If this keeps happening, try using a different browser or disable VPN.
            </p>
          )}
        </div>
      )}
    </div>
  );
});

TurnstileCaptcha.displayName = 'TurnstileCaptcha';

// Hook for using CAPTCHA with API calls
export const useCaptcha = () => {
  const [config, setConfig] = useState({ enabled: false, site_key: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/captcha/config`);
        if (res.ok) {
          const data = await res.json();
          setConfig(data);
        }
      } catch (err) {
        console.error('Failed to fetch CAPTCHA config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  return { ...config, loading };
};

export default TurnstileCaptcha;
